// App.js
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { theme } from './theme/theme';
import MainLayout from './layouts/MainLayout';
import { Layout } from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import { useEffect, lazy, Suspense } from 'react';
import { checkBackendHealth } from './utils/axios';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

// Critical path — keep static imports
import Login from './pages/Login';
import Register from './pages/Register';

// Lazy-loaded pages — split into separate chunks.
//
// lazyWithReload: after every frontend deploy the hashed chunk filenames
// change. A tab that is still running the previous main.js then requests a
// chunk URL that no longer exists; the static host answers with index.html
// ("Uncaught SyntaxError: Unexpected token '<'" + ChunkLoadError) and the page
// dies — e.g. clicking "create project" right after a deploy. One forced
// reload fetches the new manifest; the sessionStorage timestamp guards
// against a reload loop if the chunk is genuinely missing.
const lazyWithReload = (importer) => lazy(() =>
  importer().catch((error) => {
    const key = 'chunk_reload_at';
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 30000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
      return new Promise(() => {}); // keep Suspense pending while reloading
    }
    throw error;
  })
);

const Profile = lazyWithReload(() => import('./pages/Profile'));
const Projects = lazyWithReload(() => import('./pages/Projects'));
const ProjectDetail = lazyWithReload(() => import('./pages/ProjectDetailWrapper'));
const Interview = lazyWithReload(() => import('./pages/Interview'));
const EmailVerification = lazyWithReload(() => import('./pages/EmailVerification'));
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithReload(() => import('./pages/ResetPassword'));
const ForgotUsername = lazyWithReload(() => import('./pages/ForgotUsername'));
const ResendVerification = lazyWithReload(() => import('./pages/ResendVerification'));
const InterviewGuide = lazyWithReload(() => import('./pages/InterviewGuide'));
const ConversationDetail = lazyWithReload(() => import('./pages/ConversationDetail'));
const AudioInterview = lazyWithReload(() => import('./pages/AudioInterview'));
const LiveSupervision = lazyWithReload(() => import('./pages/LiveSupervision'));
const CareerProjectSetup = lazyWithReload(() => import('./pages/CareerProjectSetup'));
const CareerAssessment = lazyWithReload(() => import('./pages/CareerAssessment'));
const Home = lazyWithReload(() => import('./pages/Home'));
const CareerProjectDetail = lazyWithReload(() => import('./pages/CareerProjectDetail'));


const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress />
  </Box>
);

// Route-based page titles — covers all pages automatically
const PAGE_TITLES = {
  '/login': 'Log In | Qualitati',
  '/register': 'Sign Up | Qualitati',
  '/profile': 'Profile | Qualitati',
  '/projects': 'Projects | Qualitati',
  '/guide': 'Interview Guide | Qualitati',
  '/forgot-password': 'Forgot Password | Qualitati',
  '/reset-password': 'Reset Password | Qualitati',
  '/forgot-username': 'Forgot Username | Qualitati',
  '/verify-email': 'Verify Email | Qualitati',
  '/resend-verification': 'Resend Verification | Qualitati',
};

// Pattern-based titles for dynamic routes
const PATTERN_TITLES = [
  { pattern: /^\/projects\/[^/]+\/supervision/, title: 'Live Supervision | Qualitati' },
  { pattern: /^\/projects\/[^/]+\/conversations/, title: 'Interview Detail | Qualitati' },
  { pattern: /^\/projects\/[^/]+/, title: 'Project | Qualitati' },
  { pattern: /^\/interview\//, title: 'Interview | Qualitati' },
  { pattern: /^\/audio-interview\//, title: 'Interview | Qualitati' },
];

function useDocumentTitle() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname;
    let title = PAGE_TITLES[path];
    if (!title) {
      for (const { pattern, title: t } of PATTERN_TITLES) {
        if (pattern.test(path)) { title = t; break; }
      }
    }
    if (title) document.title = title;
  }, [location.pathname]);
}

function AppContent() {
  useDocumentTitle();
  // activity tracking removed: backend endpoint not part of this tenant
  return (
    <Routes>
      {/* All routes — wrapped in MainLayout */}
      <Route path="/*" element={
        <MainLayout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Advisor home — no marketing site, root goes straight to projects */}
              {/* Landing page; logged-in advisors are redirected to /projects inside Home */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:uuid" element={<ProjectDetail />} />
              <Route path="/projects/:uuid/supervision" element={<PrivateRoute><LiveSupervision /></PrivateRoute>} />
              <Route path="/projects/:uuid/supervision/:interviewUuid" element={<PrivateRoute><LiveSupervision /></PrivateRoute>} />
              <Route path="/projects/:uuid/conversations/:interviewId" element={<PrivateRoute><ConversationDetail /></PrivateRoute>} />
              <Route path="/career/new" element={<PrivateRoute><CareerProjectSetup /></PrivateRoute>} />
              <Route path="/career/:uuid" element={<PrivateRoute><CareerProjectDetail /></PrivateRoute>} />
              <Route path="/career/:uuid/edit" element={<PrivateRoute><CareerProjectSetup /></PrivateRoute>} />
              {/* Full v5 interview configuration for the career project — same component as /projects/:uuid */}
              <Route path="/career/:uuid/interview" element={<PrivateRoute><ProjectDetail /></PrivateRoute>} />
              <Route path="/assessment/:shareUrl" element={<CareerAssessment />} />
              <Route path="/interview/:shareUrl" element={<Interview />} />
              <Route path="/audio-interview/:shareUrl" element={<AudioInterview />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-username" element={<ForgotUsername />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/guide" element={<PrivateRoute><Layout><InterviewGuide /></Layout></PrivateRoute>} />
              <Route path="*" element={
                <Box sx={{ textAlign: 'center', py: 10 }}>
                  <Typography variant="h4" gutterBottom>Page not found</Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>The page you are looking for does not exist.</Typography>
                  <Link to="/projects" style={{ color: '#1976d2', textDecoration: 'none' }}>Go back to projects</Link>
                </Box>
              } />
            </Routes>
          </Suspense>
        </MainLayout>
      } />
    </Routes>
  );
}

const App = () => {
  useEffect(() => {
    // 检查后端健康状态
    const checkHealth = async () => {
      const status = await checkBackendHealth();
      if (process.env.NODE_ENV === 'development') console.log('[Backend Status]', status);

      if (!status.isHealthy) {
        console.error('Backend is not healthy!', status);
        // 这里可以添加错误提示UI
      }
    };

    checkHealth();
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <AppContent />
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
